import axios, { type AxiosResponse } from 'axios';
import flatten from 'lodash-es/flatten';
import * as moment from 'moment';
import {
  all,
  call,
  delay,
  put,
  race,
  select,
  take,
  takeLatest,
  type TakeEffect,
} from 'redux-saga/effects';

import type Order from '~/types/Order';
import type Transaction from '~/types/Transaction';

import { ActionKeys } from '~/actions/constants';
import * as ordersActions from '~/actions/orders.actions';
import * as ordersBatchActions from '~/actions/orders-batch.actions';
import * as shipmentsActions from '~/actions/shipments.actions';
import * as transactionsActions from '~/actions/transactions.actions';
import { setIdVerificationStatus } from '~/actions/user.actions';
import { countries } from '~/constants/countries';
import {
  MONTHLY_LABEL_LIMIT_EXCEEDED,
  UNABLE_TO_PROCESS_REQUEST,
  UNEXPECTED_ERROR,
  VERIFICATION_REQUIRED_ERROR,
} from '~/constants/errors/apiMessageErrorCodes';
import { FIRST_LABEL_PURCHASED } from '~/constants/gtm';
import { type ShippingRiskAssessment } from '~/modules/shippingRiskAssessment/types/shippingRiskAssessment.type';
import { type LabelPurchaseEventProperties } from '~/pages/Orders/Blackbeard/BlackbeardOrdersCheckout/utils';
import { type OrdersBatchState } from '~/reducers/orders-batch';
import { selectState } from '~/sagas';
import { getBatchEntryData, getOrderObjectIdByBatchEntry } from '~/selectors/batch-entry.selectors';
import { isAutoOrderEmailEnabled } from '~/selectors/notifications.selectors';
import {
  getIsSidePanelCheckout,
  getOrdersData,
  getOrdersRemainingForLabelDownload,
} from '~/selectors/orders.selectors';
import {
  getBatchCurrentStep,
  getBatchEntries,
  getBatchId,
  getBatchShipments,
  getBatchStore,
  getBatchTransactionsData,
  getFailedBatchEntriesMessages,
  getWasOrdersFetchTriggered,
} from '~/selectors/orders-batch.selectors';
import { getRatesData } from '~/selectors/rates.selectors';
import { getShipmentsData } from '~/selectors/shipments.selectors';
import {
  getFirstSuccessfulTransactionObjectId,
  getSuccessfulTransactionsCount,
  getTransactionsData,
} from '~/selectors/transactions.selectors';
import { getDaysSinceJoined, getHasDefaultMergedLabels } from '~/selectors/user.selectors';
import { sendGTMEvent } from '~/services/gtm.service';
import * as ordersService from '~/services/orders.service';
import { getVerificationStatus } from '~/services/user.service';
import {
  fromJSON as fromIDVerificationJSON,
  type JSONIDVerification,
} from '~/types/IDVerification';
import { type OrderSelection } from '~/types/Order';
import * as ordersBatch from '~/types/OrdersBatch';
import { TransactionStatus } from '~/types/Transaction';
import { APIErrorCategory } from '~/utils/APIErrorClassification/APIErrorClassification';
import { defaultQueryClient } from '~/utils/ShippoQueryClientProvider';
import { trackingHelper } from '~/utils/TrackingHelper';
import { RATES_UPDATED_EVENT, type TrackingReference } from '~/utils/TrackingHelper/types';
import { FeatureFlags, decideFeatureFlag } from '~/utils/featureflags';
import { track as optimizelyTrack } from '~/utils/featureflags/track';
import { FeatureFlagEvents } from '~/utils/featureflags/types';
import { listToMapById, listToMapByObjectId } from '~/utils/list';
import { newRelic } from '~/utils/newRelic/newRelic';
import { observability } from '~/utils/observability';
import { Events, track } from '~/utils/segment';

import { synchronizeOrdersV2 } from '../orders/orders-v2-sync.util';
import { batchTransactionsErrorHandler } from './orders-batch.util';

export function* fetchBatchOrdersData(
  action: ReturnType<typeof ordersBatchActions.fetchOrdersBatchSuccess>,
) {
  const wasOrdersFetchTriggered: boolean = yield selectState<boolean>(getWasOrdersFetchTriggered);
  const ordersFetched: string[] = [];

  if (wasOrdersFetchTriggered) return;

  const batchOrders: ordersBatch.BatchOrder[] = action.payload.batch.orders;
  const storedOrders: Record<string, Order> =
    yield selectState<Record<string, Order>>(getOrdersData);

  // Get batch entries from the payload, they are not in the store yet
  const batchEntriesByOrderObjectId = action.payload.batch.batchEntries.reduce<
    Record<string, ordersBatch.BatchEntry>
  >(
    (hashMap, batchEntry) => ({
      ...hashMap,
      [batchEntry.orderObjectId]: batchEntry,
    }),
    {},
  );

  for (const batchOrder of batchOrders) {
    const batchEntry = batchEntriesByOrderObjectId[batchOrder.objectId];

    if (
      !storedOrders[batchOrder.objectId] &&
      batchEntry.objectStatus !== ordersBatch.BatchEntryStatus.EXCLUDED_ORDER
    ) {
      ordersFetched.push(batchOrder.objectId);
      yield put(ordersActions.fetchOrder(batchOrder.objectId));
    }
  }

  yield put(ordersBatchActions.ordersFetchTriggered(ordersFetched));
}

export function* fetchOrderShipmentRates(
  action: ReturnType<typeof ordersBatchActions.fetchOrdersBatchSuccess>,
) {
  const currentBatchStep: ordersBatch.BatchSteps =
    yield selectState<ordersBatch.BatchSteps>(getBatchCurrentStep);
  const newBatch = action.payload.batch;
  const currentShipments = action.payload.currentShipments;
  const allShipmentsNew = listToMapByObjectId(newBatch.shipments);
  let shipmentsToFetch: string[] = [];

  // We won't check for the step right away since it could be
  // in the third step and we would need to fetch the shipments.
  // This code fetches the shipments only once when we are at the
  // LABELS step.
  if (
    currentBatchStep === ordersBatch.BatchSteps.LABELS &&
    Object.keys(currentShipments!).length === 0 &&
    newBatch.shipmentGroupStatus === ordersBatch.BatchGroupStatus.COMPLETE
  ) {
    for (const objectId of Object.keys(allShipmentsNew)) {
      const trackingReference = trackingHelper.getTrackingReference(
        RATES_UPDATED_EVENT.name,
        'other',
        {
          rateIds: undefined,
        },
      );
      yield put(shipmentsActions.fetchShipment(objectId, trackingReference));
    }
  }

  // If the active step is not the rates one. Do nothing.
  if (currentBatchStep !== ordersBatch.BatchSteps.RATES) return;

  // If it's the first time the batch is fetched (no shipments in the store)
  // and shipments are done we get them
  if (
    Object.keys(currentShipments!).length === 0 ||
    newBatch.shipmentGroupStatus === ordersBatch.BatchGroupStatus.COMPLETE
  ) {
    shipmentsToFetch = Object.keys(allShipmentsNew!);
  } else {
    shipmentsToFetch = Object.keys(currentShipments!).reduce(
      (shipmentsList: string[], currentShipmentKey: string) => {
        const shipment: ordersBatch.BatchShipment = currentShipments![currentShipmentKey];
        const newShipment: ordersBatch.BatchShipment = allShipmentsNew[currentShipmentKey];

        if (!newShipment) return shipmentsList;

        return shipment.numRates !== newShipment.numRates
          ? [...shipmentsList, newShipment.objectId]
          : shipmentsList;
      },
      [],
    );
  }

  for (const objectId of shipmentsToFetch) {
    const trackingReference = trackingHelper.getTrackingReference(
      RATES_UPDATED_EVENT.name,
      'other',
      {
        rateIds: undefined,
      },
    );
    yield put(shipmentsActions.fetchShipment(objectId, trackingReference));
  }

  if (newBatch.shipmentGroupStatus === ordersBatch.BatchGroupStatus.COMPLETE)
    yield put(ordersBatchActions.pollBatchOrdersEnd());
}

function* batchWorker(batchId: number, trackingReference?: TrackingReference) {
  while (true) {
    const batchShipments: OrdersBatchState['shipments'] =
      yield selectState<OrdersBatchState['shipments']>(getBatchShipments);
    const batchEntries: OrdersBatchState['batchEntries'] =
      yield selectState<OrdersBatchState['batchEntries']>(getBatchEntries);
    const batchTransactionsData: OrdersBatchState['transactions']['data'] =
      yield selectState<OrdersBatchState['transactions']['data']>(getBatchTransactionsData);

    try {
      let response: AxiosResponse<ordersBatch.JSONOrdersBatch> = yield call(
        ordersService.getBatch,
        batchId,
      );

      /**
       * The batch endpoint sometimes returns the batch group as complete but with no transactions, so we need to poll until we get the transactions.
       * Ideally, this should be fixed in the backend, or with a more sophisticated polling mechanism in the UI.
       * Given we're going to replace label purchasing on Orders V2, this workaround is fine for now.
       */
      if (
        response.data.transaction_group_status === ordersBatch.BatchGroupStatus.COMPLETE &&
        response.data.transactions.length === 0
      ) {
        const MAX_RETRIES = 3;
        let retries = 0;
        while (retries < MAX_RETRIES) {
          yield delay(1000);
          response = yield call(ordersService.getBatch, batchId);
          if (response.data.transactions.length > 0) break;
          retries++;
        }

        if (response.data.transactions.length === 0) {
          newRelic.noticeError(
            `Batch returned complete with no transactions after ${MAX_RETRIES} retries`,
            {
              batchId,
              location: 'batchWorker',
            },
          );
        }
      }

      const payload: ordersBatchActions.FetchOrdersBatchPayload = {
        batch: ordersBatch.fromJSON(response.data),
        currentBatchEntries: batchEntries.data,
        currentShipments: batchShipments.data,
        currentTransactions: batchTransactionsData,
        trackingReference,
      };
      yield put(ordersBatchActions.fetchOrdersBatchSuccess(payload));
      yield delay(500);
    } catch (err) {
      yield put(ordersBatchActions.fetchOrdersBatchError());
    }
  }
}

export const getTransactionsToFetch = ({
  allTransactionsNew,
  currentTransactions,
}: {
  allTransactionsNew: Record<string, ordersBatch.BatchTransaction>;
  currentTransactions: Record<string, ordersBatch.BatchTransaction> | undefined;
}) =>
  Object.keys(allTransactionsNew).reduce<ordersBatch.BatchTransaction[]>((list, objectId) => {
    const newTransaction: ordersBatch.BatchTransaction = allTransactionsNew[objectId];
    const currentTransaction: ordersBatch.BatchTransaction = currentTransactions![objectId];

    // TODO: Check that this ones are not already in the store
    if (
      !currentTransaction ||
      currentTransaction.objectStatus !== newTransaction.objectStatus ||
      // We need the transaction with error state as well
      newTransaction.objectStatus === TransactionStatus.ERROR ||
      // We need the transaction with queued state as well
      newTransaction.objectStatus === TransactionStatus.QUEUED
    )
      list.push(newTransaction);

    return list;
  }, []);

export const getBulkTransactionStatus = <T extends { objectStatus: TransactionStatus }>(
  transactions: T[],
) => {
  if (!transactions.length) return 'none';

  return transactions.every((t) => t.objectStatus === transactions[0].objectStatus)
    ? transactions[0].objectStatus
    : 'mixed';
};

const getBulkTransactionStatusAnyError = <T extends { objectStatus: TransactionStatus }>(
  transactions: T[],
) => {
  if (!transactions.length) return false;

  return transactions.some((t) => TransactionStatus.ERROR === t.objectStatus);
};

export const getTransactionsRateIds = (
  transactionObjects: { [transactionId: string]: Readonly<Transaction> },
  transactionIds: string[],
): string[] =>
  transactionIds.reduce(
    (rateIds: string[], transactionId: string) =>
      transactionObjects[transactionId]
        ? [transactionObjects[transactionId].rate, ...rateIds]
        : rateIds,
    [],
  );

export const getSelectedRateIds = (
  trackingReference: TrackingReference,
  selectedRates,
  batchEntryData,
): string[] => {
  if (!trackingReference.trackedOrderDetailsByShipment) return [];
  const batchEntryIds = Object.keys(trackingReference.trackedOrderDetailsByShipment);
  return batchEntryIds
    ? batchEntryIds.map((id) => selectedRates[batchEntryData[id].shipmentObjectId])
    : [];
};

const isAnyReturnQueuedTransactions = (
  transactions: ordersBatch.BatchTransaction[],
  batchEntries: ordersBatch.BatchEntry[],
) => {
  // Create a key value map for batch entries
  const batchEntriesMap = listToMapById(batchEntries);
  return transactions.some((transaction) => {
    const batchEntry = batchEntriesMap[transaction?.batchEntryId];
    return (
      batchEntry && batchEntry?.isReturn && transaction?.objectStatus === TransactionStatus.QUEUED
    );
  });
};

export function* fetchBatchOrderLabels(
  action: ReturnType<typeof ordersBatchActions.fetchOrdersBatchSuccess>,
) {
  const { batch, currentTransactions, trackingReference } = action.payload;
  const currentBatchStep: ordersBatch.BatchSteps =
    yield selectState<ordersBatch.BatchSteps>(getBatchCurrentStep);
  // If the active step is not the labels one. Do nothing.
  if (
    currentBatchStep !== ordersBatch.BatchSteps.ORDERS_PAGE &&
    currentBatchStep !== ordersBatch.BatchSteps.LABELS
  )
    return;

  const allTransactionsNew = listToMapByObjectId(batch.transactions);
  const transactionsToFetch = getTransactionsToFetch({
    allTransactionsNew,
    currentTransactions,
  });

  for (const transaction of transactionsToFetch as ordersBatch.BatchTransaction[]) {
    yield put(transactionsActions.fetchTransaction(transaction.objectId));

    if (
      currentBatchStep === ordersBatch.BatchSteps.ORDERS_PAGE &&
      transaction.objectStatus !== TransactionStatus.QUEUED
    ) {
      const orderObjectId: Maybe<string> = yield select(getOrderObjectIdByBatchEntry, {
        batchEntryId: transaction.batchEntryId,
      });
      if (orderObjectId) {
        yield put(ordersActions.fetchOrder(orderObjectId));
        yield put(ordersActions.fetchOrderBatchEntry(orderObjectId));
      }
    }
  }

  const hasSingleTransactionError =
    transactionsToFetch.length === 1 &&
    transactionsToFetch[0].objectStatus === TransactionStatus.ERROR;

  const outboundGroupIsCompleted =
    batch.transactionGroupStatus === ordersBatch.BatchGroupStatus.COMPLETE;
  const returnGroupIsCompleted =
    batch.returnTransactionGroupStatus === ordersBatch.BatchGroupStatus.NOT_APPLICABLE ||
    batch.returnTransactionGroupStatus === ordersBatch.BatchGroupStatus.COMPLETE;
  const isSidePanelCheckout: boolean = yield selectState<boolean>(getIsSidePanelCheckout);
  // outboundGroupIsCompleted and returnGroupIsCompleted can't decide for return label
  // so we are also checking if there is any queued transaction for return batch entries
  const anyQueuedReturnLabelTransaction = isAnyReturnQueuedTransactions(
    transactionsToFetch,
    batch?.batchEntries,
  );
  if (outboundGroupIsCompleted && returnGroupIsCompleted && !anyQueuedReturnLabelTransaction) {
    if (!hasSingleTransactionError) {
      const hasAutomatedEmailNotifications: boolean =
        yield selectState<boolean>(isAutoOrderEmailEnabled);

      const hasMergedDocuments: boolean = yield selectState<boolean>(getHasDefaultMergedLabels);
      if (trackingReference && transactionsToFetch.length > 0) {
        const successFullTransactionCount: number = yield selectState<number>(
          getSuccessfulTransactionsCount,
        );
        const isFirstLabelPurchase = successFullTransactionCount === 0;
        if (isFirstLabelPurchase) {
          yield put(transactionsActions.updateSuccessfulTransactionsCount(1));
          yield put(
            transactionsActions.updateFirstSuccessfulTransactionObjectId(
              batch.transactions[0].objectId,
            ),
          );
        }
        if (getBulkTransactionStatusAnyError(transactionsToFetch) && isSidePanelCheckout) {
          yield put(
            ordersActions.confirmCheckoutWithErrors(
              hasAutomatedEmailNotifications,
              hasMergedDocuments,
            ),
          );
        } else {
          yield put(
            ordersActions.confirmCheckout(hasAutomatedEmailNotifications, hasMergedDocuments),
          );
        }
      } else {
        yield put(
          ordersActions.confirmCheckout(hasAutomatedEmailNotifications, hasMergedDocuments),
        );
      }
    }

    if (hasSingleTransactionError)
      if (isSidePanelCheckout) yield put(ordersActions.confirmCheckoutWithErrors());

    yield call(synchronizeOrdersV2, {
      orderObjectIds: batch.orders.map((order) => order.objectId),
    });
    yield put(ordersBatchActions.pollBatchOrdersEnd());
  }
}

export function* batchWatcher() {
  while (true) {
    const action: TakeEffect = yield take(ActionKeys.ORDERS_BATCH_POLL_START);

    const result: { timeout: boolean } = yield race({
      cancel: take([ActionKeys.ORDERS_BATCH_POLL_END]),
      task: call(
        batchWorker,
        (action as unknown as ReturnType<typeof ordersBatchActions.pollBatchOrdersStart>).payload
          .batchId,
        (action as unknown as ReturnType<typeof ordersBatchActions.pollBatchOrdersStart>).payload
          .trackingReference,
      ),
      timeout: delay(CONFIG.batchPollTimeout, true),
    });

    if (result.timeout) yield put(ordersBatchActions.pollBatchOrdersTimeout());
  }
}

export function* createTransactions(
  action: ReturnType<typeof ordersBatchActions.createBatchTransactions>,
) {
  const { trackingReference } = action.payload;
  try {
    const excludedAndIgnoredOrders = [];
    const result: Awaited<ReturnType<typeof ordersService.createBatchTransactions>> = yield call(
      ordersService.createBatchTransactions,
      action.payload.batchId,
      action.payload.rates,
      excludedAndIgnoredOrders,
    );

    yield put(
      ordersBatchActions.fetchTransactionData(
        result?.data.remaining_labels_until_limit,
        result?.data.label_limit_renewal_date,
      ),
    );

    yield put(ordersBatchActions.createBatchTransactionsSuccess(action.payload.rates.length));

    const batchId: null | number | undefined = yield selectState<null | number | undefined>(
      getBatchId,
    );
    // The compiler thinks this might be missing
    // Try to do it without assertion.
    yield put(ordersBatchActions.pollBatchOrdersStart(batchId as number, trackingReference));
  } catch (err) {
    if (!err.response) {
      yield put(ordersBatchActions.createBatchTransactionsError(UNEXPECTED_ERROR));
      return;
    }

    // Handle API Errors
    batchTransactionsErrorHandler(action, err);

    const data = err.response.data;
    if (!data) return;

    const {
      detail,
      label_limit_renewal_date,
      remaining_labels_until_limit,
      requires_verification,
    } = data;

    if (requires_verification) {
      const idvResult: AxiosResponse<JSONIDVerification> = yield call(getVerificationStatus);
      const verification = fromIDVerificationJSON(idvResult.data);
      yield put(setIdVerificationStatus(verification));

      yield put(ordersBatchActions.createBatchTransactionsError(VERIFICATION_REQUIRED_ERROR));
      return;
    }

    if (remaining_labels_until_limit) {
      yield put(
        ordersBatchActions.fetchTransactionData(
          remaining_labels_until_limit,
          label_limit_renewal_date,
        ),
      );
    }

    if (detail) yield put(ordersBatchActions.createBatchTransactionsError(detail));
    else if (remaining_labels_until_limit)
      yield put(ordersBatchActions.createBatchTransactionsError(MONTHLY_LABEL_LIMIT_EXCEEDED));
  }
}

export function* generatePackingSlips() {
  try {
    const currentBatchStep: ordersBatch.BatchSteps =
      yield selectState<ordersBatch.BatchSteps>(getBatchCurrentStep);
    // If the active step is not the labels one. Do nothing.
    if (
      currentBatchStep !== ordersBatch.BatchSteps.ORDERS_PAGE &&
      currentBatchStep !== ordersBatch.BatchSteps.LABELS
    )
      return;

    yield put(ordersBatchActions.generatePackingSlipsFromBatch());
    const batchStore: OrdersBatchState = yield selectState<OrdersBatchState>(getBatchStore);
    const batchOrders = batchStore?.batchEntries?.byOrderObjectId;
    // We need to download slips for all the selected orders along with
    // just purchased order, if there are selected orders
    const remainingOrders: OrderSelection = yield selectState<OrderSelection>(
      getOrdersRemainingForLabelDownload,
    );

    // This happens when some of the label purchase had failed and we retry
    // but we still need to download the slips for the ones that were successful
    // at the end
    const allOrders = Object.keys(remainingOrders).length
      ? { ...remainingOrders, ...batchOrders }
      : batchOrders;
    const orderObjectIDs = Object.keys(allOrders);
    const batchEntryIDs = orderObjectIDs.reduce<number[]>(
      (accum, orderObjectID) => [...accum, ...allOrders[orderObjectID]],
      [],
    );
    // Filter out orders with no batch entry ids because batch entries are already considered
    // batchEntryIDs and we don't want duplicate slips
    const orderObjectIDsForLabels = orderObjectIDs.filter(
      (orderObjectID) => !allOrders[orderObjectID] || allOrders[orderObjectID].length === 0,
    );
    const response: AxiosResponse<{ slip_url: string }> = yield call(
      ordersService.getOrderPackingSlip,
      orderObjectIDsForLabels,
      batchEntryIDs,
    );
    yield put(
      ordersBatchActions.generatePackingSlipsFromBatchSuccess({
        slipUrl: response?.data?.slip_url,
      }),
    );
  } catch (err) {
    yield put(ordersBatchActions.generatePackingSlipsFromBatchError(UNEXPECTED_ERROR));
  }
}

export function* generateAllLabels() {
  try {
    const currentBatchStep: ordersBatch.BatchSteps =
      yield selectState<ordersBatch.BatchSteps>(getBatchCurrentStep);
    // If the active step is not the labels one. Do nothing.
    if (
      currentBatchStep !== ordersBatch.BatchSteps.ORDERS_PAGE &&
      currentBatchStep !== ordersBatch.BatchSteps.LABELS
    )
      return;

    yield put(ordersBatchActions.generateAllLabels());
    const batchStore: OrdersBatchState = yield selectState<OrdersBatchState>(getBatchStore);
    const batchOrders = batchStore?.batchEntries?.byOrderObjectId;
    // We need to download labels for all the selected orders along with
    // just purchased order, if there are selected orders
    const remainingOrders: OrderSelection = yield selectState<OrderSelection>(
      getOrdersRemainingForLabelDownload,
    );

    // This happens when some of the label purchase had failed and we retry
    // but we still need to download the labels for the ones that were successful
    // at the end
    const allOrders = Object.keys(remainingOrders).length
      ? { ...remainingOrders, ...batchOrders }
      : batchOrders;

    const orderObjectIDs = Object.keys(allOrders);
    const batchEntryIDs = orderObjectIDs.reduce<number[]>(
      (accum, orderObjectID) => [...accum, ...allOrders[orderObjectID]],
      [],
    );
    const response: AxiosResponse<{ url: string }> = yield call(
      ordersService.getAllLabels,
      orderObjectIDs,
      batchEntryIDs,
    );
    yield put(ordersBatchActions.generateAllLabelsSuccess({ labelsUrl: response.data.url }));
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 403) {
      observability.singleEvent('LABEL_DOWNLOAD_BLOCKED');
      yield put(ordersBatchActions.generateAllLabelsError('Label Download Unauthorized'));
    } else if (error.response && error.response.data) {
      yield put(ordersBatchActions.generateAllLabelsError(error.response.data));
    } else {
      yield put(ordersBatchActions.generateAllLabelsError(UNEXPECTED_ERROR));
    }
  }
}

export function* fetchOrdersErrors(
  action: ReturnType<typeof ordersBatchActions.fetchOrdersBatchSuccess>,
) {
  const currentBatchEntriesList = Object.values(action.payload.currentBatchEntries || {});
  const newBatchEntries = listToMapById(action.payload.batch.batchEntries);
  let batchEntriesToFetch: ordersBatch.BatchEntry[] = [];

  if (currentBatchEntriesList.length === 0) {
    batchEntriesToFetch = Object.values(newBatchEntries).filter(ordersBatch.isFailedBatchEntry);
  } else {
    batchEntriesToFetch = currentBatchEntriesList.reduce(
      (list: ordersBatch.BatchEntry[], batchEntry) => {
        const newBatchEntry = newBatchEntries[batchEntry.id];

        if (
          batchEntry.objectStatus !== newBatchEntry.objectStatus &&
          ordersBatch.isFailedBatchEntry(newBatchEntry)
        )
          return [...list, newBatchEntry];

        return list;
      },
      [],
    );
  }

  for (const batchEntry of batchEntriesToFetch) {
    yield put(ordersActions.fetchOrder(batchEntry.orderObjectId));
    if (batchEntry.objectStatus === ordersBatch.BatchEntryStatus.TRANSACTION_REQUEST_FAILED)
      yield put(ordersActions.retryShipment({ [batchEntry.orderObjectId]: [batchEntry.id] }));
  }
}

export function* createAndBuyBatch(
  action: ReturnType<typeof ordersBatchActions.createAndBuyBatch>,
) {
  const { trackingReference } = action.payload;
  const batchEntryIds = flatten(Object.values(action.payload.orderSelection));

  try {
    // Send create batch request. Order Ids can be empty since those are only in this service for oldx
    const response: AxiosResponse<ordersBatch.JSONOrdersBatch> = yield call(
      ordersService.createBatch,
      [],
      batchEntryIds,
    );
    const parsedBatch = ordersBatch.fromJSON(response.data);
    yield put(ordersActions.createBatchSuccess(parsedBatch));

    // Buy rates
    yield put(
      ordersBatchActions.createBatchTransactions(
        parsedBatch.id,
        action.payload.rateObjectIds,
        trackingReference,
        batchEntryIds,
      ),
    );
    observability.successEvent('RETURN_WORKFLOW_CREATE_LABEL');
  } catch (err) {
    newRelic.noticeError('Failed to create and buy batch', {
      error: err,
      location: 'createAndBuyBatch',
    });
    // Api Error Handling
    if (axios.isAxiosError(err) && err.response && err.response.config?.url) {
      if (err.response.config.url === '/batch/') {
        batchEntryIds.forEach((batchEntryId) => {
          observability.errorEvent('LABEL_PURCHASED', {
            batchEntryId,
            errorCategory: APIErrorCategory.BATCH_API,
            errorCodes: 'Unknown',
            errorCount: 1,
            errorMessages: UNABLE_TO_PROCESS_REQUEST,
            errorSources: 'Shippo',
            id: `${batchEntryId}`,
          });
        });
      }
    }

    const errorMessage = UNABLE_TO_PROCESS_REQUEST;
    observability.errorEvent('RETURN_WORKFLOW_CREATE_LABEL', { errorMessage });
    yield put(ordersActions.createBatchError(errorMessage));
  }
}

function* ordersBatchFetchSuccess(
  action: ReturnType<typeof ordersBatchActions.fetchOrdersBatchSuccess>,
) {
  yield all([
    call(fetchBatchOrdersData, action),
    call(fetchBatchOrderLabels, action),
    call(fetchOrderShipmentRates, action),
    call(fetchOrdersErrors, action),
  ]);

  try {
    yield call(trackLabelPurchase, action);
  } catch (error) {
    newRelic.noticeError('Failed to track Label Purchased event', { error });
  }
}

const hoursSince = (date: moment.Moment) => {
  const now = moment();
  return Math.round(now.diff(date, 'hours', true));
};

const transactionMap = {};

export function* trackLabelPurchase(
  action: ReturnType<typeof ordersBatchActions.fetchOrdersBatchSuccess>,
) {
  const { currentTransactions, trackingReference } = action.payload;
  if (!currentTransactions || !Object.keys(currentTransactions).length) return;

  const batchEntryData = yield selectState(getBatchEntryData);
  const ratesData = yield selectState(getRatesData);
  const shipmentData = yield selectState(getShipmentsData);
  const daysSinceJoined = yield selectState(getDaysSinceJoined);
  const transactionData = yield selectState(getTransactionsData);
  const ordersData = yield selectState(getOrdersData);
  const failedBatchEntriesMessages = yield selectState(getFailedBatchEntriesMessages);
  const firstSuccessfulTransactionObjectId = yield selectState(
    getFirstSuccessfulTransactionObjectId,
  );
  const isSidePanelCheckout = yield selectState(getIsSidePanelCheckout);
  const isBulkOrder = Object.keys(currentTransactions).length > 1;

  for (const [transactionId, transaction] of Object.entries(currentTransactions)) {
    if (transactionMap[transactionId]) continue;

    const batchEntry = batchEntryData[transaction.batchEntryId];
    const { rate: rateId } = transactionData[transactionId] || {};
    const rate = ratesData[rateId];
    if (!rate) continue;

    const shipment = shipmentData[rate?.shipment];
    const order = ordersData[batchEntry?.orderObjectId];

    const message = failedBatchEntriesMessages[batchEntry?.orderObjectId];
    if (message) continue;

    const isFirstLabelPurchase =
      !firstSuccessfulTransactionObjectId || transactionId === firstSuccessfulTransactionObjectId;

    const isCrossBorder =
      batchEntry?.addressFrom?.country?.iso2 !== batchEntry?.addressTo?.country?.iso2;
    const isShippingToUS = batchEntry?.addressTo?.country?.iso2 === countries.US.iso2;

    const timeToPurchase = order.placedAt ? hoursSince(order.placedAt) : undefined;

    const shippingRiskFlag = decideFeatureFlag(FeatureFlags.SHIPPING_RISK_ASSESSMENT_EXPERIMENT_V2);
    const address = batchEntry?.addressTo;
    const shippingRiskAssessment: string =
      defaultQueryClient.getQueryData<ShippingRiskAssessment>([
        'shippingRiskAssessment',
        address,
        address?.zip?.substring(0, 5),
      ])?.risk_level ?? 'N/A';

    const eventProps: LabelPurchaseEventProperties = {
      carrier: rate.provider,
      contains_alcohol: !!batchEntry?.extras?.alcohol,
      contains_dry_ice: !!batchEntry?.extras?.dryIce,
      create_return_label: !!batchEntry?.extras?.isReturnRequested,
      currency: rate.currency,
      custom_declared: !batchEntry?.extras?.isBypassCustoms,
      days_since_joined: daysSinceJoined || 0,
      error: !!order.errors.length,
      has_insurance: rate.insurance || +(rate.includedInsurancePrice || '0') > 0,
      insurance_cost: rate.includedInsurancePrice || '0',
      insured_amount: batchEntry.extras?.insurance?.amount || '0',
      is_bulk_order: isBulkOrder,
      is_crossborder: isCrossBorder,
      is_first_label_purchase: isFirstLabelPurchase,
      is_hazmat: !!batchEntry?.extras?.hazmat?.isHazmat,
      is_hazmat_biomaterial: !!batchEntry?.extras?.hazmat?.isBiologicalMaterial,
      is_hazmat_lithiumbatteries: !!batchEntry?.extras?.hazmat?.isLithiumBatteries,
      is_shipping_to_us: isShippingToUS,
      location: isSidePanelCheckout ? 'side_panel' : 'order_details',
      order_details_version: isSidePanelCheckout
        ? undefined
        : trackingReference?.appLocation === 'order_details_mui'
        ? 'mui'
        : 'legacy',
      rate_id: rateId,
      rate_type: rate.attributes.join(' '),
      rate_types_offered: (shipment.rates || []).flatMap((rate) => rate.attributes || []).join(' '),
      service_level: rate.servicelevel.name,
      shipment_date: shipment.shipmentDate.toISOString(),
      shipment_price: rate.amount,
      shipping_risk: shippingRiskAssessment,
      shipping_risk_variation: shippingRiskFlag?.variation || 'not-included',
      time_to_purchase_hours: timeToPurchase,
      transaction_id: transactionId,
    };

    transactionMap[transactionId] = true;

    yield call(track, Events.label_purchase, eventProps);
    yield call(
      optimizelyTrack<LabelPurchaseEventProperties>,
      FeatureFlagEvents.LABEL_PURCHASED,
      eventProps,
    );

    if (isFirstLabelPurchase) {
      yield call(sendGTMEvent, {
        eventName: FIRST_LABEL_PURCHASED,
      });
      observability.singleEvent('FIRST_LABEL_PURCHASED');
    }

    defaultQueryClient.invalidateQueries({ queryKey: ['transactions', 'count'] });
  }
}

export function* synchronizeConfirmRecipient(
  action: ReturnType<typeof ordersBatchActions.confirmBatchEntryRecipient>,
) {
  const { orderObjectId } = action.payload;
  yield call(synchronizeOrdersV2, { orderObjectIds: [orderObjectId] });
}

export default [
  batchWatcher(),
  takeLatest(ActionKeys.ORDERS_BATCH_FETCH_SUCCESS, ordersBatchFetchSuccess),
  takeLatest(ActionKeys.ORDERS_BATCH_CREATE_TRANSACTIONS, createTransactions),
  takeLatest(ActionKeys.ORDERS_BATCH_POLL_END, generatePackingSlips),
  takeLatest(ActionKeys.ORDERS_BATCH_POLL_END, generateAllLabels),
  takeLatest(ActionKeys.ORDERS_BATCH_CREATE_AND_BUY, createAndBuyBatch),
  takeLatest(ActionKeys.ORDERS_BATCH_ENTRY_CONFIRM_RECIPIENT, synchronizeConfirmRecipient),
];
